import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Inbox, Send, FileText, Search, Briefcase, X, Loader2, Reply, Edit3,
  Archive, AlertCircle, CornerUpLeft, Command, Forward, Trash2, Ban, ShieldCheck, AlertTriangle, Sparkles,
  Folder, FolderInput, FolderPlus, Mail, MailOpen, Check, Undo2,
} from 'lucide-react';
import mailApi from '../../services/mailApi';
import useMailWebSocket from '../../hooks/useMailWebSocket';
import { buildHtmlSrcDoc } from './emailUtils';

// OuijaMail M0 — three-pane, Virgo-tokened. Reads from the Postgres MIRROR
// (~130ms) instead of JMAP (~4.5s over the mesh) — profiled 2026-07-20 — with an
// in-session message cache and hover-prefetch; JMAP stays the live-push channel.
// Case-context rail + list chips come free from the mirror's case_id.

// The rail is derived from Stalwart's mailbox list (GET /api/mail/mailboxes), not
// from a hardcoded array — ~86 per-client folders were invisible because this file
// shipped seven names. These icons are only a lookup for the role mailboxes; every
// other folder renders with the generic one.
const ROLE_ICON = {
  inbox: Inbox, sent: Send, drafts: FileText, archive: Archive,
  junk: Ban, trash: Trash2, quarantine: ShieldCheck,
};
const PAGE = 50;   // not the API's 200 cap: each row Fernet-decrypts a preview

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
};
const caseLabel = (c) => {
  if (!c) return '';
  const num = c.display_number || c.case_number || '';
  const name = c.case_name || c.name || '';
  return name ? `${name} (${num})` : `${num}`;
};
const StatusDot = ({ status }) => {
  const active = (status || '').toLowerCase() === 'active';
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-400' : 'bg-virgo-teal-700'}`} />;
};
// why a suggestion appears — corroboration is honest about the signal, not louder
const CONF_NOTE = { corroborated: 'subject corroborates', disambiguated: 'matched by subject', 'case-number': 'matched by case #' };
// M2 sort tag: the 3-tier bucket + which tier produced it (contact/bulk = free,
// ai = paid Haiku, ai_cached = reused verdict). Honest about the source.
const CAT_STYLE = {
  legal: ['Legal', 'bg-virgo-teal-700/40 text-virgo-teal-200'],
  important: ['Important', 'bg-virgo-gold-500/25 text-virgo-gold-300'],
  advertising: ['Ads', 'bg-slate-600/40 text-slate-300'],
  personal: ['Personal', 'bg-indigo-500/25 text-indigo-200'],
  notifications: ['Notice', 'bg-sky-600/25 text-sky-200'],
  other: ['Other', 'bg-virgo-navy-700 text-virgo-teal-400'],
  uncategorized: ['Unsorted', 'bg-virgo-navy-700 text-virgo-teal-600'],
};
const SRC_LABEL = { contact: 'matched a contact (free)', bulk: 'bulk sender (free)', ai: 'AI · Haiku (paid)', ai_cached: 'AI · cached (free)', capped: 'AI budget reached' };
const CategoryChip = ({ category, source }) => {
  const [label, cls] = CAT_STYLE[category] || [category, 'bg-virgo-navy-700 text-virgo-teal-400'];
  return (
    <span title={`Sorted: ${SRC_LABEL[source] || source || ''}`} className={`inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded ${cls}`}>
      {label}{source === 'ai' && <Sparkles size={8} className="opacity-70" />}
    </span>
  );
};
const Chip = ({ c, suggested }) => (
  <span title={suggested ? ('Suggested matter (not filed) — File to confirm' + (CONF_NOTE[c && c.confidence] ? ' · ' + CONF_NOTE[c.confidence] : '')) : 'Filed to this matter'}
    className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded max-w-[12rem] truncate ${suggested ? 'border border-dashed border-virgo-teal-600/60 text-virgo-teal-400' : 'bg-virgo-teal-700/30 text-virgo-teal-300'}`}>
    <Briefcase size={10} className="shrink-0" />{caseLabel(c)}{suggested && <span className="opacity-60 ml-0.5">?</span>}
  </span>
);

export default function OuijaMailM0() {
  const [folder, setFolder] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sel, setSel] = useState(null);        // opened message (full)
  const [thread, setThread] = useState(null);
  const [openLoading, setOpenLoading] = useState(false);
  const [live, setLive] = useState(false);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const [cursor, setCursor] = useState(0);
  const [showSheet, setShowSheet] = useState(false);
  const [filePicker, setFilePicker] = useState(false);
  const [compose, setCompose] = useState(null);
  const [toast, setToast] = useState(null);
  const [cases, setCases] = useState([]);
  const [sentStatus, setSentStatus] = useState(null);   // per-recipient status for an open Sent message
  const [matterFilter, setMatterFilter] = useState(null);   // client-side lens: display_number or null
  const [matterCtx, setMatterCtx] = useState(null);   // {deadlines, docs_count} for the open matched message
  const [action, setAction] = useState(null);   // {type:'time'|'feed', case_id, ...}
  const [folderCounts, setFolderCounts] = useState({});   // per-folder sidebar badge counts (M3)
  const [rail, setRail] = useState({ roles: [], folders: [], diagnostics: [] });
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pending, setPending] = useState([]);      // live-push rows held behind the pill
  const [searchTotal, setSearchTotal] = useState(null);
  const [folderPicker, setFolderPicker] = useState(false);   // move-to-any-folder
  const [bulkPicker, setBulkPicker] = useState(false);       // bulk move-to-any-folder
  const [newFolder, setNewFolder] = useState('');
  const [aiUsage, setAiUsage] = useState(null);  // M2 sort meter (firm month spend/cap + rates)
  const [sorting, setSorting] = useState(false);

  const searchRef = useRef(null);
  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  const cache = useRef(new Map());   // id -> {message, thread}
  // Generation counter: a folder switch invalidates every request in flight, so a
  // slow page-2 of Inbox cannot append itself into Junk.
  const genRef = useRef(0);
  // The cursor anchors to a message ID, not a list index. Any mutation (a merged
  // push, an archive, a filed message) shifts indices; an index cursor silently
  // points at a different message afterwards.
  const cursorIdRef = useRef(null);
  const rowsRef = useRef([]);
  const inSearch = results !== null;
  const rawList = inSearch ? results : messages;
  const matterOf = (m) => m.case || m.suggested || null;
  const list = matterFilter ? rawList.filter((m) => (matterOf(m) || {}).display_number === matterFilter) : rawList;
  const mattersInView = useMemo(() => {
    const map = {};
    rawList.forEach((m) => { const c = matterOf(m); if (c && c.display_number) map[c.display_number] = c; });
    return Object.values(map);
  }, [rawList]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 3200); };

  // No per-folder memory of loaded depth: switching back re-reads page 1. Holding
  // seven folders' buffers means stale rows and unbounded growth, and the ledger
  // in the footer is what makes depth legible, not a hoarded buffer.
  const switchFolder = useCallback((id) => {
    genRef.current += 1;                       // invalidate anything in flight
    setFolder(id); setMessages([]); setTotal(null); setHasMore(false);
    setPending([]); setSel(null); setThread(null); setResults(null);
    setSearchTotal(null); setQuery(''); setCursor(0); setMatterFilter(null);
    cursorIdRef.current = null; rowsRef.current = [];
    if (listRef.current) listRef.current.scrollTop = 0;
  }, []);
  const casesById = useMemo(() => { const m = {}; cases.forEach((c) => { m[c.id] = c; }); return m; }, [cases]);

  const loadFolder = useCallback(async (f, { append = false } = {}) => {
    const gen = append ? genRef.current : ++genRef.current;
    if (append) setLoadingMore(true); else { setError(''); setLoading(true); }
    try {
      const off = append ? rowsRef.current.length : 0;
      const { data } = await mailApi.get('/api/mail/mirror/inbox',
        { params: { folder: f, limit: PAGE, offset: off } });
      if (gen !== genRef.current) return;          // a folder switch overtook us
      const batch = data.messages || [];
      setMessages((prev) => {
        const base = append ? prev : [];
        const seen = new Set(base.map((m) => m.id));
        // Dedupe on append: offset paging over a live stream re-serves a row when
        // new mail arrives between pages. (Keyset is the exact answer; not today.)
        return base.concat(batch.filter((m) => !seen.has(m.id)));
      });
      setTotal(data.total);
      setHasMore(batch.length > 0 && off + batch.length < (data.total || 0));
    } catch (e) {
      if (gen === genRef.current) setError(`Could not load ${f}: ${e?.response?.data?.detail || e.message}`);
    } finally {
      if (gen === genRef.current) { setLoading(false); setLoadingMore(false); }
    }
  }, []);

  const loadMore = useCallback(() => {
    if (loadingMore || loading || !hasMore || inSearch) return;
    loadFolder(folder, { append: true });
  }, [loadingMore, loading, hasMore, inSearch, folder, loadFolder]);

  const loadRail = useCallback(() => {
    mailApi.get('/api/mail/mailboxes')
      .then(({ data }) => setRail(data || { roles: [], folders: [], diagnostics: [] }))
      .catch((e) => setError(`Could not read the folder list: ${e?.response?.data?.detail || e.message}`));
  }, []);

  const loadCounts = useCallback(() => {
    mailApi.get('/api/mail/folder-counts').then(({ data }) => setFolderCounts(data || {})).catch(() => {});
  }, []);
  const loadUsage = useCallback(() => {
    mailApi.get('/api/mail/ai-usage').then(({ data }) => setAiUsage(data)).catch(() => {});
  }, []);
  const sortFolder = async () => {
    setSorting(true);
    try {
      const { data } = await mailApi.post(`/api/mail/classify?folder=${folder}&limit=200`);
      await loadFolder(folder); loadUsage(); loadCounts();
      flash(`Sorted ${data.processed}: ${data.free} free, ${data.paid_calls} AI${data.capped ? ' — budget reached' : ''}.`);
    } catch (e) { flash(`Sort failed: ${e?.response?.data?.detail || e.message}`); }
    finally { setSorting(false); }
  };
  const raiseCap = async () => {
    try { await mailApi.post('/api/mail/ai-usage/raise', { add_cents: 2000 }); loadUsage(); flash('Cap raised +$20 — paid sorting resumed.'); }
    catch { flash('Could not raise the cap.'); }
  };

  useEffect(() => { loadUsage(); }, [loadUsage]);
  useEffect(() => { loadRail(); }, [loadRail]);
  // keep a ref of the rendered rows so paging/keys read current data without
  // re-creating callbacks on every keystroke
  useEffect(() => { rowsRef.current = list; }, [list]);

  // Infinite scroll. The footer button is not a fallback for looks: an
  // IntersectionObserver never fires on a list that doesn't overflow.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || inSearch) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((x) => x.isIntersecting)) loadMore();
    }, { root: listRef.current, rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore, inSearch, folder]);

  // Re-anchor the cursor by message id after any list mutation.
  useEffect(() => {
    const id = cursorIdRef.current;
    if (id == null) return;
    const i = list.findIndex((m) => m.id === id);
    if (i >= 0 && i !== cursor) setCursor(i);
  }, [list]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (list[cursor]) cursorIdRef.current = list[cursor].id; }, [cursor, list]);
  useEffect(() => { loadFolder(folder); }, [folder, loadFolder]);
  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => {
    mailApi.get('/api/virgo/cases', { params: { limit: 500 } })
      .then(({ data }) => setCases(Array.isArray(data) ? data : (data.cases || data.items || []))).catch(() => {});
  }, []);

  const mergeNew = useCallback(async () => {
    // Fetch page 0 and merge only rows we don't have. Never reload the folder:
    // that would discard everything paged in below.
    try {
      const { data } = await mailApi.get('/api/mail/mirror/inbox',
        { params: { folder, limit: PAGE, offset: 0 } });
      setTotal(data.total);
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const fresh = (data.messages || []).filter((m) => !seen.has(m.id));
        return fresh.length ? fresh.concat(prev) : prev;
      });
      setPending([]);
    } catch { /* keep what we have */ }
  }, [folder]);

  useMailWebSocket({
    onConnect: () => setLive(true),
    onDisconnect: () => setLive(false),
    onJmapStateChange: async () => {
      if (inSearch) return;
      const atTop = (listRef.current?.scrollTop || 0) < 40;
      if (atTop) { mergeNew(); loadRail(); return; }
      // Scrolled down: don't move rows under the cursor. Count what's new and
      // offer it; clicking merges, it never reloads.
      try {
        const { data } = await mailApi.get('/api/mail/mirror/inbox',
          { params: { folder, limit: PAGE, offset: 0 } });
        const seen = new Set(rowsRef.current.map((m) => m.id));
        const fresh = (data.messages || []).filter((m) => !seen.has(m.id));
        if (fresh.length) { setPending(fresh); setTotal(data.total); }
      } catch { /* silent */ }
    },
  });

  // paint the row's read state without a refetch
  const paintRead = useCallback((mid, isRead) => {
    const put = (m) => (m.id === mid ? { ...m, is_read: isRead } : m);
    setMessages((ms) => ms.map(put));
    setResults((rs) => (rs ? rs.map(put) : rs));
    const entry = cache.current.get(mid);
    if (entry?.message) entry.message.is_read = isRead;
  }, []);

  // READ / UNREAD -- writes $seen to Stalwart FIRST, so the state is real in every
  // other client. Paint optimistically, roll the row back if Stalwart refuses:
  // a read state the server never accepted is exactly the lie this replaces.
  const setRead = useCallback(async (mid, isRead, { quiet = false } = {}) => {
    if (!mid) return false;
    paintRead(mid, isRead);
    try {
      const { data } = await mailApi.post('/api/mail/read', { email_id: mid, is_read: isRead });
      if (!quiet) {
        flash(data?.synced_to_stalwart === false
          ? `Marked ${isRead ? 'read' : 'unread'} in Virgo only — ${data.reason}.`
          : `Marked ${isRead ? 'read' : 'unread'}.`);
      }
      loadCounts(); loadRail();
      return true;
    } catch (e) {
      paintRead(mid, !isRead);   // Stalwart said no — do not keep the optimistic paint
      if (!quiet) flash(`Could not mark ${isRead ? 'read' : 'unread'}: ${e?.response?.data?.detail || e.message}`);
      return false;
    }
  }, [paintRead, loadCounts, loadRail]);

  const fetchFull = useCallback(async (row) => {
    if (cache.current.has(row.id)) return cache.current.get(row.id);
    const { data } = await mailApi.get(`/api/emails/${row.id}`);
    const caseInfo = row.case || (data.case_id ? casesById[data.case_id] : null);
    const entry = {
      message: {
        id: row.id, subject: data.subject, fromText: data.from, toText: data.to,
        body: data.content || '', receivedAt: data.received_at,
        case_id: data.case_id, caseInfo, filed: !!data.case_id, mirror_id: row.id,
        thread_id: row.thread_id, suggested: row.suggested,
      },
      thread: null,
    };
    cache.current.set(row.id, entry);
    return entry;
  }, [casesById]);

  const openMessage = useCallback(async (row, idx) => {
    if (typeof idx === 'number') setCursor(idx);
    setThread(null); setOpenLoading(true);
    setSel({ id: row.id, subject: row.subject, fromText: row.from, caseInfo: row.case, suggested: row.suggested, filed: !!row.case_id, __loading: true });
    try {
      const entry = await fetchFull(row);
      setSel(entry.message);
      if (row.thread_id) {
        if (entry.thread) setThread(entry.thread);
        else mailApi.get(`/api/mail/mirror/thread/${row.thread_id}`).then((t) => {
          entry.thread = t.data; setThread(t.data);
        }).catch(() => {});
      }
      // opening IS reading — push $seen to Stalwart so the other clients agree.
      // quiet: no toast for an implicit action, and a failure here must not
      // make the message look unopenable. The row simply stays bold.
      if (row.is_read === false) setRead(row.id, true, { quiet: true });
    } catch (e) {
      setError(`Could not open message: ${e?.response?.data?.detail || e.message}`); setSel(null);
    } finally { setOpenLoading(false); }
  }, [fetchFull, setRead]);

  const prefetch = useCallback((row) => { if (!cache.current.has(row.id)) fetchFull(row).catch(() => {}); }, [fetchFull]);

  const runSearch = useCallback(async (e) => {
    if (e) e.preventDefault();
    const q = query.trim();
    if (!q) { setResults(null); return; }
    setSearching(true); setError(''); setSel(null); setThread(null); setCursor(0);
    try {
      const { data } = await mailApi.get('/api/mail/search', { params: { q, limit: PAGE, offset: 0 } });
      setSearchTotal(data.total ?? null);
      // normalize search hits to the mirror-row shape
      setResults((data.emails || []).map((m) => ({
        id: m.id, from: m.from, subject: m.subject, preview: m.body_preview,
        received_at: m.received_at, is_read: m.is_read, thread_id: null,
        case_id: null, case: null,
      })));
    } catch (err) {
      setError(`Search failed: ${err?.response?.data?.detail || err.message}`); setResults([]);
    } finally { setSearching(false); }
  }, [query]);
  const clearSearch = () => {
    setQuery(''); setResults(null); setSearchTotal(null);
    setSel(null); setThread(null); setCursor(0); cursorIdRef.current = null;
  };

  const loadMoreSearch = useCallback(async () => {
    if (searching || !results || searchTotal == null || results.length >= searchTotal) return;
    setSearching(true);
    try {
      const { data } = await mailApi.get('/api/mail/search',
        { params: { q: query.trim(), limit: PAGE, offset: results.length } });
      setResults((prev) => {
        const seen = new Set((prev || []).map((m) => m.id));
        return (prev || []).concat((data.emails || [])
          .filter((m) => !seen.has(m.id))
          .map((m) => ({ id: m.id, from: m.from, subject: m.subject, preview: m.body_preview,
            received_at: m.received_at, is_read: m.is_read, thread_id: null,
            case_id: null, case: null })));
      });
    } catch (e) {
      setError(`Search paging failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSearching(false); }
  }, [searching, results, searchTotal, query]);

  const openCompose = useCallback(async (opts = {}) => {
    let sigHtml = '';
    try { const { data } = await mailApi.get('/api/user/mail-settings/signature'); sigHtml = data?.signature || ''; } catch { /* optional */ }
    setCompose({ to: opts.to || '', subject: opts.subject || '', body: opts.body || '', signatureHtml: sigHtml, status: null, error: '' });
  }, []);
  const replyTo = useCallback(() => {
    if (!sel) { openCompose(); return; }
    const subj = sel.subject || '';
    const addr = (sel.fromText || '').match(/<([^>]+)>/)?.[1] || sel.fromText || '';
    openCompose({ to: addr, subject: subj.match(/^re:/i) ? subj : `Re: ${subj}` });
  }, [sel, openCompose]);
  const forwardMsg = useCallback(() => {
    if (!sel) { openCompose(); return; }
    const subj = sel.subject || '';
    const quoted = `\n\n---------- Forwarded ----------\nFrom: ${sel.fromText || ''}\nSubject: ${subj}\n\n${sel.body || ''}`;
    openCompose({ subject: subj.match(/^fwd:/i) ? subj : `Fwd: ${subj}`, body: quoted });
  }, [sel, openCompose]);

  // Poll per-recipient delivery status (queued/transmitted -> delivered/bounced) from the log correlation.
  const pollStatus = useCallback(async (emailId, tries = 0) => {
    try {
      const { data } = await mailApi.get(`/api/mail/send-status/${emailId}`);
      setCompose((c) => (c && c.sent ? { ...c, sent: { ...c.sent, recipients: data.recipients, telemetry_ok: data.telemetry_ok } } : c));
      const terminal = (data.recipients || []).every((r) => ['delivered', 'bounced', 'rejected', 'failed'].includes(r.status));
      if (!terminal && tries < 8) setTimeout(() => pollStatus(emailId, tries + 1), 2500);
    } catch { /* keep last-known; never fabricate */ }
  }, []);

  const sendCompose = useCallback(async () => {
    if (!compose) return;
    const to = compose.to.split(',').map((s) => s.trim()).filter(Boolean);
    if (!to.length || !compose.subject.trim()) { setCompose((c) => ({ ...c, error: 'A recipient and subject are required.' })); return; }
    setCompose((c) => ({ ...c, status: 'sending', error: '' }));
    try {
      const esc = (x) => (x || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      // Signature -> text keeps its LINES (the old stripTags collapsed a multi-line
      // signature onto one line). Same shape as OuijaMail: "-- " separator, then the lines.
      const sigToText = (h) => (h || '')
        .replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|li|tr|h[1-6])\s*>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&')
        .replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
      const sig = compose.signatureHtml || '';
      const bodyText = compose.body + (sig ? `\n\n-- \n${sigToText(sig)}` : '');
      const bodyHtml = `<div style="white-space:pre-wrap;font-family:Arial,sans-serif;">${esc(compose.body)}</div>` + (sig ? `<br>-- <br>${sig}` : '');
      const { data } = await mailApi.post('/api/mail/send', { to, subject: compose.subject, body: bodyText, body_html: bodyHtml });
      setCompose((c) => ({ ...c, status: 'sent', sent: { email_id: data.email_id, recipients: data.recipients, telemetry_ok: null } }));
      flash('Message sent — tracking delivery.');
      pollStatus(data.email_id);
      if (folder === 'sent') loadFolder('sent');
    } catch (e) {
      setCompose((c) => ({ ...c, status: 'failed', error: `Send failed: ${e?.response?.data?.detail || e.message}. Nothing was sent — fix and try again.` }));
    }
  }, [compose, pollStatus, folder, loadFolder]);

  // ---- selection state (page-select vs matching-select are DIFFERENT things) --
  // `selected` is ids the user actually saw and picked. `matchAll` arms the
  // other mode: a filter descriptor + expected count that the SERVER re-runs and
  // aborts on drift. They are never the same control and never the word "all"
  // unqualified -- that confusion is how someone trashes four thousand messages
  // meaning to trash fifty.
  const [selected, setSelected] = useState(() => new Set());
  const [selAnchor, setSelAnchor] = useState(null);
  const [matchAll, setMatchAll] = useState(null);   // {folder, expected}
  const [guardPrompt, setGuardPrompt] = useState(null);
  const [lastOp, setLastOp] = useState(null);       // {op_id, moved} -> Undo
  const clearSel = useCallback(() => { setSelected(new Set()); setSelAnchor(null); setMatchAll(null); }, []);
  useEffect(() => { clearSel(); }, [folder, inSearch, clearSel]);

  const toggleSel = useCallback((mid, idx, shiftKey, listNow) => {
    setMatchAll(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (shiftKey && selAnchor != null) {
        const [a, b] = [Math.min(selAnchor, idx), Math.max(selAnchor, idx)];
        for (let k = a; k <= b; k++) if (listNow[k]) next.add(listNow[k].id);
      } else if (next.has(mid)) next.delete(mid);
      else next.add(mid);
      return next;
    });
    setSelAnchor(idx);
  }, [selAnchor]);

  // optimistic removal from the current list after a Stalwart-confirmed move
  const dropFromList = useCallback((mid) => {
    setMessages((ms) => ms.filter((m) => m.id !== mid));
    setResults((rs) => (rs ? rs.filter((m) => m.id !== mid) : rs));
    cache.current.delete(mid);
    setSel(null); setThread(null);
  }, []);

  // FILE-TO-MATTER: tag case_id AND move OUT of inbox to Archive (Stalwart-first)
  const fileTo = useCallback(async (c) => {
    const mid = sel?.mirror_id || sel?.id;
    if (!mid) { setFilePicker(false); return; }
    try {
      await mailApi.post('/api/mail/file', { email_id: mid, case_id: c.id });
      const caseInfo = { display_number: c.display_number || c.case_number, case_name: c.case_name || c.name, status: c.status };
      setFilePicker(false); dropFromList(mid);
      flash(`Filed to ${caseLabel(caseInfo)} — moved out of your inbox (kept under the matter).`);
      loadFolder(folder); loadCounts();
    } catch (e) { flash(`Could not file: ${e?.response?.data?.detail || e.message}`); }
  }, [sel, folder, dropFromList, loadFolder, loadCounts]);

  // MOVE to a role mailbox (archive / trash / junk / inbox). Stalwart-first.
  // Filing to `Blanton` is the same operation as filing to Trash — both are just a
  // mailbox on the server, so both go through the same endpoint.
  const moveMsg = useCallback(async (target, label) => {
    const mid = sel?.mirror_id || sel?.id;
    if (!mid) return;
    const note = { archive: 'Archived.', trash: 'Moved to Trash.', junk: 'Marked spam — moved to Junk.', inbox: 'Moved to Inbox.' }[target]
      || `Filed to ${label || target}.`;
    try {
      await mailApi.post('/api/mail/move-to', { email_id: mid, target });
      dropFromList(mid); flash(note); setFolderPicker(false); loadCounts(); loadRail();
    } catch (e) { flash(`Move failed: ${e?.response?.data?.detail || e.message}`); }
  }, [sel, dropFromList, loadCounts, loadRail]);

  // ---- single-row move with optimistic ROLLBACK (drag target) ---------------
  // Same shape as the read-state toggle: paint first, put it back if Stalwart
  // refuses. The row is re-inserted at its old index, not appended.
  const moveRow = useCallback(async (mid, target, label) => {
    const idx = messages.findIndex((x) => x.id === mid);
    const snap = idx >= 0 ? messages[idx] : null;
    const ridx = results ? results.findIndex((x) => x.id === mid) : -1;
    const rsnap = ridx >= 0 ? results[ridx] : null;
    setMessages((ms) => ms.filter((x) => x.id !== mid));
    if (results) setResults((rs) => rs.filter((x) => x.id !== mid));
    if (sel && (sel.mirror_id || sel.id) === mid) { setSel(null); setThread(null); }
    try {
      await mailApi.post('/api/mail/move-to', { email_id: mid, target });
      flash(`Filed to ${label || target}.`); loadCounts(); loadRail();
    } catch (e) {
      if (snap) setMessages((ms) => { const a = [...ms]; a.splice(Math.min(idx, a.length), 0, snap); return a; });
      if (rsnap) setResults((rs) => { const a = [...(rs || [])]; a.splice(Math.min(ridx, a.length), 0, rsnap); return a; });
      flash(`Move failed — message put back: ${e?.response?.data?.detail || e.message}`);
    }
  }, [messages, results, sel, loadCounts, loadRail]);

  // ---- bulk move -------------------------------------------------------------
  const reloadAfterBulk = useCallback(() => {
    clearSel(); loadCounts(); loadRail(); loadFolder(folder);
  }, [clearSel, loadCounts, loadRail, loadFolder, folder]);

  const bulkMove = useCallback(async (target, label, acknowledge = false, idsOverride = null) => {
    // idsOverride: drag-drop passes its ids explicitly -- reading `selected` from
    // the closure right after setSelected would see the STALE set.
    const body = (matchAll && !idsOverride)
      ? { target, filter: { folder: matchAll.folder }, expected_count: matchAll.expected,
          acknowledge_contacts: acknowledge }
      : { target, email_ids: idsOverride || [...selected], acknowledge_contacts: acknowledge };
    try {
      const { data } = await mailApi.post('/api/mail/move-bulk', body);
      setLastOp({ op_id: data.op_id, moved: data.moved });
      flash(data.failed
        ? `Moved ${data.moved} of ${data.requested} to ${label || target}. ${data.failed} FAILED (ids logged).`
        : `Moved ${data.moved} to ${label || target}.`);
      setGuardPrompt(null); reloadAfterBulk();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === 'known_contacts') setGuardPrompt({ target, label, detail: d, ids: idsOverride || null });
      else if (d?.error === 'count_changed') {
        flash(`The matching set changed: ${d.actual} now match (was ${d.expected}). Nothing moved — re-select.`);
        reloadAfterBulk();
      } else flash(`Bulk move failed: ${d?.message || d || e.message}`);
    }
  }, [matchAll, selected, reloadAfterBulk]);

  const undoLast = useCallback(async () => {
    if (!lastOp) return;
    try {
      const { data } = await mailApi.post(`/api/mail/bulk-ops/${lastOp.op_id}/undo`);
      flash(`Restored ${data.restored} message(s) to their prior folders${data.failed ? ` — ${data.failed} FAILED` : ''}.`);
      setLastOp(null); reloadAfterBulk();
    } catch (e) { flash(`Undo failed: ${e?.response?.data?.detail || e.message}`); }
  }, [lastOp, reloadAfterBulk]);

  // drop handler: one id -> the optimistic single move; several -> the journaled
  // bulk endpoint (guard and all). Role entries move by role alias, named folders
  // by their Stalwart display name (resolve_target_mailbox matches it exactly).
  const onDropIds = useCallback((f, ids) => {
    const target = f.role ? f.key : (f.name || f.key);
    if (ids.length === 1) moveRow(ids[0], target, f.name);
    else bulkMove(target, f.name, false, ids);
  }, [moveRow, bulkMove]);

  const createFolder = useCallback(async () => {
    const name = newFolder.trim();
    if (!name) return;
    try {
      const { data } = await mailApi.post('/api/mail/folders', { name });
      setNewFolder(''); loadRail();
      flash(data.created ? `Folder “${name}” created.` : `Folder “${name}” already existed.`);
      if (sel) moveMsg(data.id, name);
    } catch (e) { flash(`Could not create folder: ${e?.response?.data?.detail || e.message}`); }
  }, [newFolder, loadRail, sel, moveMsg]);

  useEffect(() => {
    const onKey = (e) => {
      if (compose) { if (e.key === 'Escape') setCompose(null); return; }
      if (filePicker) { if (e.key === 'Escape') setFilePicker(false); return; }
      if (e.key === '?') { e.preventDefault(); setShowSheet((s) => !s); return; }
      if (e.key === 'Escape') { setShowSheet(false); return; }
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) { if (e.key === 'Enter' && e.target === searchRef.current) runSearch(); return; }
      if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); return; }
      if (e.key === 'c') { e.preventDefault(); openCompose(); return; }
      if (e.key === 'r') { e.preventDefault(); replyTo(); return; }
      if (e.key === 'f') { e.preventDefault(); forwardMsg(); return; }
      if (e.key === 'j' || e.key === 'k') {
        e.preventDefault();
        if (e.key === 'j' && cursor >= list.length - 1) {
          // At the bottom, j pages instead of dead-ending — the keyboard alone
          // reaches the last row of the folder.
          if (inSearch) loadMoreSearch(); else loadMore();
          return;
        }
        setCursor((i) => Math.min(list.length - 1, Math.max(0, i + (e.key === 'j' ? 1 : -1))));
        return;
      }
      if (e.key === 'Enter') { e.preventDefault(); if (list[cursor]) openMessage(list[cursor], cursor); return; }
      if (e.key === 'e') { e.preventDefault(); if (sel) moveMsg('archive'); return; }
      if (e.key === '#') { e.preventDefault(); if (sel) moveMsg('trash'); return; }
      // 'u' toggles read on whatever is focused: the open message, else the
      // cursor row -- so a list can be marked read without opening each one.
      if (e.key === 'u') {
        e.preventDefault();
        const t = sel ? { id: sel.mirror_id || sel.id } : list[cursor];
        if (!t) return;
        const cur = list.find((m) => m.id === t.id);
        setRead(t.id, cur ? cur.is_read === false : true);
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [list, cursor, compose, filePicker, openMessage, openCompose, replyTo, runSearch,
      loadMore, loadMoreSearch, inSearch, sel, moveMsg, setRead]);
  useEffect(() => { const el = listRef.current?.querySelector(`[data-row="${cursor}"]`); if (el) el.scrollIntoView({ block: 'nearest' }); }, [cursor]);

  // When a Sent message is open, show its honest per-recipient delivery status.
  useEffect(() => {
    setSentStatus(null);
    if (sel && sel.id && folder === 'sent' && !inSearch) {
      mailApi.get(`/api/mail/send-status/${sel.id}`).then(({ data }) => setSentStatus(data)).catch(() => {});
    }
  }, [sel, folder, inSearch]);

  // Case-context rail: for a matched (filed or suggested) open message, pull deadlines + docs.
  const matchedCaseId = sel && (sel.case_id || (sel.suggested && sel.suggested.case_id));
  useEffect(() => {
    setMatterCtx(null);
    if (matchedCaseId) mailApi.get(`/api/mail/matter/${matchedCaseId}`).then(({ data }) => setMatterCtx(data)).catch(() => {});
  }, [matchedCaseId]);

  const submitAction = useCallback(async () => {
    if (!action) return;
    try {
      if (action.type === 'time') {
        const hours = parseFloat(action.hours);
        if (!hours || !action.description?.trim()) { setAction((a) => ({ ...a, error: 'Hours and a description are required.' })); return; }
        await mailApi.post(`/api/cases/${action.case_id}/time-entries`, { description: action.description, hours });
        flash('Time logged to the matter.');
      } else if (action.type === 'feed') {
        if (!action.content?.trim()) { setAction((a) => ({ ...a, error: 'Write something to post.' })); return; }
        await mailApi.post(`/api/cases/${action.case_id}/feed`, { content: action.content });
        flash('Posted to the case feed.');
      }
      setAction(null);
    } catch (e) { setAction((a) => ({ ...a, error: `Failed: ${e?.response?.data?.detail || e.message}` })); }
  }, [action]);

  return (
    <div className="flex h-full bg-virgo-navy-900 text-virgo-teal-50" style={{ fontSize: 14 }}>
      {/* Folders */}
      <div className="w-44 shrink-0 border-r border-virgo-teal-700/40 bg-virgo-navy-800 flex flex-col">
        <div className="px-3 py-3 flex items-center justify-between">
          <span className="text-lg font-bold tracking-tight leading-none text-white">Ouija<span className="text-virgo-teal-400">Mail</span></span>
          <span title={live ? 'Live' : 'Reconnecting…'} className={`w-2 h-2 rounded-full ${live ? 'bg-emerald-400' : 'bg-virgo-teal-700'}`} />
        </div>
        <button onClick={() => openCompose()} className="mx-3 mb-3 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white text-sm font-medium"><Edit3 size={14} /> Compose</button>
        <nav className="px-2 space-y-0.5 text-sm flex-1 overflow-y-auto">
          <FolderGroup items={rail.roles} folder={folder} inSearch={inSearch} onPick={switchFolder} onDropIds={onDropIds} />
          {rail.folders.length > 0 && (
            <>
              <div className="pt-3 pb-1 px-2 text-[10px] uppercase tracking-wide text-virgo-teal-600">
                Folders · {rail.folders.length}
              </div>
              <FolderGroup items={rail.folders} folder={folder} inSearch={inSearch} onPick={switchFolder} onDropIds={onDropIds} />
            </>
          )}
          {rail.diagnostics.length > 0 && (
            <>
              {/* Below a separator, and NOT presented as a folder: `orphaned` is a
                  computed verdict — Stalwart has no such mailbox and nothing files
                  into it. Labelling it a folder would claim these messages are
                  filed somewhere when the actual claim is "not on the server". */}
              <div className="mt-3 pt-2 border-t border-virgo-teal-700/30">
                <div className="pb-1 px-2 text-[10px] uppercase tracking-wide text-virgo-teal-600">
                  Diagnostics
                </div>
                {rail.diagnostics.map((d) => (
                  <button key={d.key} onClick={() => switchFolder(d.key)} title={d.note}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg ${folder === d.key && !inSearch ? 'bg-virgo-navy-600 text-virgo-gold-200' : 'text-virgo-teal-600 hover:bg-virgo-navy-700'}`}>
                    <AlertTriangle size={14} />
                    <span className="flex-1 text-left">{d.name}</span>
                    <span className="text-xs">{d.mirror_count}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="px-2 pt-2 text-[9px] text-virgo-teal-700">counts: mirror</div>
        </nav>
        <div className="mt-auto px-3 py-2 border-t border-virgo-teal-700/40 space-y-1.5">
          {aiUsage && (
            <div className={`text-[10px] ${aiUsage.capped ? 'text-virgo-gold-300' : 'text-virgo-teal-500'}`} title="Firm AI mail-sort spend this calendar month">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1"><Sparkles size={11} /> AI sort</span>
                <span className="font-mono">${aiUsage.spend_dollars.toFixed(2)}/${aiUsage.cap_dollars.toFixed(0)}</span>
              </div>
              <div className="mt-1 h-1 rounded bg-virgo-navy-700 overflow-hidden">
                <div className={`h-full ${aiUsage.capped ? 'bg-virgo-gold-400' : 'bg-virgo-teal-500'}`}
                  style={{ width: `${Math.min(100, aiUsage.cap_cents ? (aiUsage.spend_cents / aiUsage.cap_cents) * 100 : 0)}%` }} />
              </div>
              <div className="mt-1 flex justify-between text-virgo-teal-600">
                <span>{Math.round(aiUsage.tier3_hit_rate * 100)}% AI · {Math.round(aiUsage.cache_hit_rate * 100)}% cached</span>
                <span>{aiUsage.free} free</span>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between text-[11px]">
            <button onClick={sortFolder} disabled={sorting} className="flex items-center gap-1 text-virgo-teal-400 hover:text-virgo-teal-200 disabled:opacity-50">
              {sorting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Sort
            </button>
            <button onClick={() => setShowSheet(true)} className="flex items-center gap-1 text-virgo-teal-500 hover:text-virgo-teal-300"><Command size={12} /> Shortcuts</button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="w-96 shrink-0 border-r border-virgo-teal-700/40 bg-virgo-navy-800 flex flex-col">
        {aiUsage?.capped && (
          <div className="m-2 p-2 rounded-lg bg-virgo-gold-500/15 border border-virgo-gold-500/40 text-virgo-gold-200 text-xs flex items-center gap-2">
            <AlertCircle size={14} className="shrink-0" />
            <span className="flex-1">AI sort paused — firm hit ${aiUsage.cap_dollars.toFixed(0)}/mo (${aiUsage.spend_dollars.toFixed(2)}). Free sorting still runs.</span>
            <button onClick={raiseCap} className="px-2 py-0.5 rounded bg-virgo-gold-500 text-virgo-navy-900 font-medium whitespace-nowrap">Raise +$20</button>
          </div>
        )}
        <div className="px-3 py-2.5 border-b border-virgo-teal-700/40">
          <form onSubmit={runSearch} className="flex items-center gap-2 bg-virgo-navy-900 rounded-lg px-2 border border-virgo-teal-700/50 focus-within:border-virgo-teal-500">
            <Search size={14} className="text-virgo-teal-500" />
            <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search all mail  ( / )" className="flex-1 bg-transparent py-1.5 text-sm focus:outline-none border-0" style={{ boxShadow: 'none' }} />
            {inSearch && <button type="button" onClick={clearSearch} className="text-virgo-teal-500 hover:text-virgo-teal-300"><X size={14} /></button>}
          </form>
          <div className="mt-1.5 text-[11px] text-virgo-teal-500">
            {inSearch
              ? (searchTotal != null && searchTotal > results.length
                  ? `${results.length} of ${searchTotal.toLocaleString()} results · all mail, full history`
                  : `${results.length} result${results.length === 1 ? '' : 's'} · all mail, full history`)
              : `${railLabel(rail, folder)}${
                  total == null ? ''
                    : list.length < total ? ` · ${list.length} of ${total.toLocaleString()}`
                    : ` · ${total.toLocaleString()}`} · mirror`}
          </div>
          {mattersInView.length > 0 && (
            <div className="mt-1.5 flex items-center gap-1 flex-wrap">
              <span className="text-[10px] text-virgo-teal-600" title="Filters the rows loaded so far, not the whole folder">Matter (in loaded):</span>
              {mattersInView.map((c) => (
                <button key={c.display_number} onClick={() => setMatterFilter(matterFilter === c.display_number ? null : c.display_number)}
                  className={`text-[10px] px-1.5 py-0.5 rounded ${matterFilter === c.display_number ? 'bg-virgo-teal-600 text-white' : 'bg-virgo-navy-700 text-virgo-teal-300 hover:bg-virgo-navy-600'}`}>
                  {c.display_number}
                </button>
              ))}
              {matterFilter && <button onClick={() => setMatterFilter(null)} className="text-[10px] text-virgo-teal-500 hover:text-virgo-teal-300">clear</button>}
            </div>
          )}
        </div>
        {/* SELECTION BAR. Page-select and matching-select are visually distinct
            controls: the page count is a checkbox with the literal loaded number;
            "all matching" is a separate amber sentence that states the count
            before it arms and is re-verified server-side at execution. */}
        {!inSearch && list.length > 0 && (
          <div className="px-3 py-1.5 border-b border-virgo-navy-700 flex items-center gap-3 text-xs">
            <span onClick={() => {
                if (selected.size === list.length) clearSel();
                else { setMatchAll(null); setSelected(new Set(list.map((m) => m.id))); }
              }}
              className="flex items-center gap-1.5 cursor-pointer text-virgo-teal-400 hover:text-virgo-teal-200">
              <span className="w-3.5 h-3.5 rounded border border-virgo-teal-600 flex items-center justify-center">
                {selected.size === list.length && list.length > 0 && <Check size={10} />}
              </span>
              Select all {list.length} loaded
            </span>
            {selected.size === list.length && list.length > 0 && total > list.length && !matchAll && (
              <button onClick={() => setMatchAll({ folder, expected: total })}
                className="text-amber-300 hover:text-amber-200 underline decoration-dotted">
                Select all {(total || 0).toLocaleString()} in {folder} instead
              </button>
            )}
            {matchAll && (
              <span className="text-amber-300 font-medium">
                ALL {(matchAll.expected).toLocaleString()} matching in {matchAll.folder} — not just this page
              </span>
            )}
            {(selected.size > 0 || matchAll) && (
              <span className="ml-auto flex items-center gap-2">
                <span className="text-virgo-teal-300">
                  {matchAll ? `${matchAll.expected.toLocaleString()} armed` : `${selected.size} selected`}
                </span>
                <button onClick={() => setBulkPicker(true)} className="px-2 py-1 rounded bg-virgo-navy-600 text-virgo-teal-200 hover:bg-virgo-navy-500">Move to…</button>
                <button onClick={() => bulkMove('archive', 'Archive')} className="px-2 py-1 rounded bg-virgo-navy-600 text-virgo-teal-200 hover:bg-virgo-navy-500">Archive</button>
                <button onClick={() => bulkMove('trash', 'Trash')} className="px-2 py-1 rounded bg-virgo-navy-600 text-red-300 hover:bg-virgo-navy-500">Trash</button>
                <button onClick={clearSel} className="text-virgo-teal-500 hover:text-virgo-teal-300">Clear</button>
              </span>
            )}
            {lastOp && !(selected.size > 0 || matchAll) && (
              <button onClick={undoLast} className="ml-auto flex items-center gap-1 text-virgo-teal-300 hover:text-white">
                <Undo2 size={12} /> Undo last bulk move ({lastOp.moved})
              </button>
            )}
          </div>
        )}
        <div ref={listRef} className="flex-1 overflow-y-auto">
          {loading && !inSearch && <div className="p-4 text-virgo-teal-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
          {searching && <div className="p-4 text-virgo-teal-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Searching the full mirror…</div>}
          {error && <div className="m-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex gap-2"><AlertCircle size={15} className="shrink-0 mt-0.5" />{error}</div>}
          {inSearch && !searching && results.length === 0 && !error && <div className="p-4 text-virgo-teal-400 text-sm">No messages match “{query}” anywhere in your mail history.</div>}
          {!inSearch && !loading && !error && messages.length === 0 && <div className="p-6 text-center text-virgo-teal-400 text-sm">Nothing in {folder}.</div>}

          {pending.length > 0 && (
            /* Scrolled away from the top: never reload and dump the loaded tail.
               The pill merges on click. */
            <button onClick={() => { setPending([]); mergeNew(); listRef.current?.scrollTo({ top: 0 }); }}
              className="sticky top-0 z-10 w-full py-1.5 bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white text-xs font-medium">
              {pending.length} new message{pending.length === 1 ? '' : 's'} — show
            </button>
          )}
          {!searching && list.map((m, i) => {
            const unread = m.is_read === false;
            const isSel = sel && sel.id === m.id;
            return (
              <button key={(inSearch ? 's' : '') + m.id} data-row={i} onClick={() => openMessage(m, i)} onMouseEnter={() => prefetch(m)}
                draggable
                onDragStart={(e) => {
                  // dragging a selected row carries the whole selection
                  const ids = selected.has(m.id) && selected.size > 1 ? [...selected] : [m.id];
                  e.dataTransfer.setData('application/x-ouija-ids', JSON.stringify(ids));
                  e.dataTransfer.effectAllowed = 'move';
                }}
                className={`w-full text-left px-3 py-2.5 border-b border-virgo-navy-700 focus:outline-none ${isSel ? 'bg-virgo-teal-700/40' : selected.has(m.id) ? 'bg-virgo-navy-600/70' : i === cursor ? 'bg-virgo-navy-700' : 'hover:bg-virgo-navy-700/60'}`}>
                <div className="flex items-center gap-2">
                  <span onClick={(e) => { e.stopPropagation(); toggleSel(m.id, i, e.shiftKey, list); }}
                    className="shrink-0 w-4 h-4 rounded border border-virgo-teal-600 flex items-center justify-center cursor-pointer hover:border-virgo-teal-300"
                    title="Select (shift-click for range)">
                    {selected.has(m.id) && <Check size={12} className="text-virgo-teal-300" />}
                  </span>
                  {unread && <span className="w-1.5 h-1.5 rounded-full bg-virgo-teal-400 shrink-0" />}
                  <span className={`truncate flex-1 ${unread ? 'font-semibold text-white' : 'text-virgo-teal-200'}`}>{m.from || '(unknown)'}</span>
                  <span className="text-[11px] text-virgo-teal-500 shrink-0">{fmtDate(m.received_at)}</span>
                </div>
                <div className={`truncate text-sm ${unread ? 'text-virgo-teal-100' : 'text-virgo-teal-400'}`}>{m.subject || '(no subject)'}</div>
                {m.misclassified && (
                  <div className="mt-0.5" title={`Sender is a contact on active matter ${m.misclassified_matter || ''} — likely not spam`}>
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-virgo-gold-500/20 text-virgo-gold-200 border border-virgo-gold-500/40">
                      <AlertTriangle size={10} /> possible misclassification{m.misclassified_matter ? ` · ${m.misclassified_matter}` : ''}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-0.5">
                  {m.category && <CategoryChip category={m.category} source={m.category_source} />}
                  {m.case ? <Chip c={m.case} /> : m.suggested ? <Chip c={m.suggested} suggested /> : null}
                  {m.preview && <span className="truncate text-xs text-virgo-teal-600 flex-1">{m.preview}</span>}
                </div>
              </button>
            );
          })}

          {/* The ledger IS the button. An IntersectionObserver never fires on a
              list that doesn't overflow, and the two numbers must always both be
              visible or the header can drift from what is reachable. */}
          {!inSearch && !loading && !error && list.length > 0 && (
            <>
              <div ref={sentinelRef} />
              <button onClick={loadMore} disabled={!hasMore || loadingMore}
                className="w-full py-2.5 text-[11px] text-virgo-teal-500 hover:text-virgo-teal-300 disabled:hover:text-virgo-teal-500 flex items-center justify-center gap-2">
                {loadingMore ? <><Loader2 size={12} className="animate-spin" /> Loading…</>
                  : hasMore ? `${list.length} of ${(total || 0).toLocaleString()} loaded · Load ${PAGE} more`
                  : `All ${(total || 0).toLocaleString()} loaded.`}
              </button>
            </>
          )}
          {inSearch && !searching && results.length > 0 && searchTotal != null && (
            <button onClick={loadMoreSearch} disabled={results.length >= searchTotal}
              className="w-full py-2.5 text-[11px] text-virgo-teal-500 hover:text-virgo-teal-300 flex items-center justify-center gap-2">
              {results.length < searchTotal
                ? `${results.length} of ${searchTotal.toLocaleString()} results · Load ${PAGE} more`
                : `All ${searchTotal.toLocaleString()} results loaded.`}
            </button>
          )}
        </div>
      </div>

      {/* Reading pane + rail */}
      <div className="flex-1 min-w-0 flex flex-col bg-virgo-navy-900">
        {!sel && <div className="flex-1 flex items-center justify-center text-virgo-teal-600 text-sm">Select a message to read it. Press <kbd className="mx-1 px-1.5 py-0.5 bg-virgo-navy-700 rounded text-virgo-teal-300">?</kbd> for shortcuts.</div>}
        {sel && (
          <div className="flex-1 overflow-y-auto">
            <div className="px-6 pt-5 pb-3 border-b border-virgo-teal-700/40">
              <div className="flex items-start justify-between gap-3">
                <div className="text-lg font-semibold text-white">{sel.subject || '(no subject)'}</div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={replyTo} title="Reply (r)" className="p-1.5 rounded-lg text-virgo-teal-300 hover:bg-virgo-navy-700"><Reply size={15} /></button>
                  <button onClick={forwardMsg} title="Forward (f)" className="p-1.5 rounded-lg text-virgo-teal-300 hover:bg-virgo-navy-700"><Forward size={15} /></button>
                  {(() => {
                    const mid = sel.mirror_id || sel.id;
                    const isRead = (messages.find((m) => m.id === mid)
                      || (results || []).find((m) => m.id === mid) || {}).is_read !== false;
                    return (
                      <button onClick={() => setRead(mid, !isRead)}
                        title={isRead ? 'Mark unread (u)' : 'Mark read (u)'}
                        className="p-1.5 rounded-lg text-virgo-teal-300 hover:bg-virgo-navy-700">
                        {isRead ? <MailOpen size={15} /> : <Mail size={15} />}
                      </button>
                    );
                  })()}
                  <button onClick={() => moveMsg('archive')} title="Archive (e)" className="p-1.5 rounded-lg text-virgo-teal-300 hover:bg-virgo-navy-700"><Archive size={15} /></button>
                  {folder === 'junk'
                    ? <button onClick={() => moveMsg('inbox')} title="Not spam — move to Inbox" className="p-1.5 rounded-lg text-emerald-300 hover:bg-virgo-navy-700"><ShieldCheck size={15} /></button>
                    : <button onClick={() => moveMsg('junk')} title="Spam — move to Junk" className="p-1.5 rounded-lg text-virgo-gold-300 hover:bg-virgo-navy-700"><Ban size={15} /></button>}
                  <button onClick={() => setFolderPicker(true)} title="File to a folder…"
                    className="p-1.5 rounded-lg text-virgo-teal-300 hover:bg-virgo-navy-700"><FolderInput size={15} /></button>
                  <button onClick={() => moveMsg('trash')} title="Trash (#)" className="p-1.5 rounded-lg text-virgo-teal-300 hover:bg-virgo-navy-700"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="mt-1 text-sm text-virgo-teal-400">{sel.fromText || '(unknown)'}{sel.receivedAt ? ` · ${new Date(sel.receivedAt).toLocaleString()}` : ''}</div>
            </div>
            <div className="px-6 py-3 bg-virgo-navy-800/60 border-b border-virgo-teal-700/40">
              {sel.__loading && <div className="text-xs text-virgo-teal-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> …</div>}
              {!sel.__loading && sel.filed && sel.caseInfo && (
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase size={15} className="text-virgo-teal-400" />
                  <span className="font-medium text-white">{caseLabel(sel.caseInfo)}</span>
                  <span className="flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-virgo-navy-700 text-virgo-teal-300"><StatusDot status={sel.caseInfo.status} />{sel.caseInfo.status || 'matter'}</span>
                </div>
              )}
              {!sel.__loading && !sel.filed && sel.suggested && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm flex items-center gap-2">
                    <Briefcase size={15} className="text-virgo-teal-400" />
                    <span className="text-virgo-teal-500">Suggested</span>
                    {CONF_NOTE[sel.suggested.confidence] && <span className="text-[10px] text-virgo-teal-600">· {CONF_NOTE[sel.suggested.confidence]}</span>}
                    <span className="font-medium text-white">{caseLabel(sel.suggested)}</span>
                  </span>
                  <span className="flex items-center gap-1 shrink-0">
                    <button onClick={() => fileTo({ id: sel.suggested.case_id, display_number: sel.suggested.display_number, case_name: sel.suggested.case_name, status: sel.suggested.status })} className="text-xs px-2.5 py-1 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white">File to this</button>
                    <button onClick={() => setFilePicker(true)} title="File to a different matter" className="text-xs px-2 py-1 rounded-lg bg-virgo-navy-700 text-virgo-teal-300 hover:bg-virgo-navy-600">Other…</button>
                  </span>
                </div>
              )}
              {!sel.__loading && !sel.filed && !sel.suggested && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-virgo-gold-400 flex items-center gap-2"><Briefcase size={15} /> Not filed to a matter</span>
                  <button onClick={() => setFilePicker(true)} className="text-xs px-2.5 py-1 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white">File to…</button>
                </div>
              )}
              {!sel.__loading && matchedCaseId && matterCtx && (
                <div className="mt-2 pt-2 border-t border-virgo-teal-700/20 flex items-center gap-3 text-xs text-virgo-teal-400 flex-wrap">
                  <span className={(matterCtx.deadlines || []).length ? 'text-virgo-gold-400' : ''}>{(matterCtx.deadlines || []).length} open deadline{(matterCtx.deadlines || []).length === 1 ? '' : 's'}</span>
                  {matterCtx.deadlines && matterCtx.deadlines[0] && (
                    <span className="text-virgo-gold-400 truncate max-w-[16rem]">next: {matterCtx.deadlines[0].type || 'deadline'}{matterCtx.deadlines[0].date ? ' · ' + new Date(matterCtx.deadlines[0].date).toLocaleDateString() : ''}</span>
                  )}
                  <span>{matterCtx.docs_count} document{matterCtx.docs_count === 1 ? '' : 's'}</span>
                  <span className="flex-1" />
                  <button onClick={() => setAction({ type: 'time', case_id: matchedCaseId, hours: '', description: sel.subject || '' })} className="px-2 py-0.5 rounded bg-virgo-navy-700 hover:bg-virgo-navy-600 text-virgo-teal-300">Log time</button>
                  <button onClick={() => setAction({ type: 'feed', case_id: matchedCaseId, content: '' })} className="px-2 py-0.5 rounded bg-virgo-navy-700 hover:bg-virgo-navy-600 text-virgo-teal-300">Post to feed</button>
                </div>
              )}
            </div>
            {sentStatus && sentStatus.recipients && sentStatus.recipients.length > 0 && (
              <div className="px-6 py-3 bg-virgo-navy-800/40 border-b border-virgo-teal-700/40">
                <div className="text-[11px] uppercase tracking-wide text-virgo-teal-500 mb-1.5">Delivery</div>
                <div className="space-y-1">
                  {sentStatus.recipients.map((r) => <RecipientStatus key={r.email} r={r} />)}
                </div>
              </div>
            )}
            <div className="px-6 py-4">
              {openLoading ? <div className="text-virgo-teal-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading message…</div>
                : (/<[a-z][\s\S]*>/i.test(sel.body || '')
                    ? <iframe title="message" sandbox="" srcDoc={buildHtmlSrcDoc(sel.body || '')} className="w-full rounded border border-virgo-teal-700/30" style={{ minHeight: 340, background: '#F3EFE7', filter: 'brightness(0.9) contrast(0.96)' }} />
                    : <pre className="whitespace-pre-wrap font-sans text-sm text-virgo-teal-100 leading-relaxed">{sel.body || '(no text body)'}</pre>)}
              {thread && thread.messages && thread.messages.length > 1 && (
                <div className="mt-8 border-t border-virgo-teal-700/40 pt-4">
                  <div className="text-xs uppercase tracking-wide text-virgo-teal-400 mb-2">Thread · {thread.messages.length} messages</div>
                  <div className="space-y-1">
                    {thread.messages.map((tm) => (
                      <button key={tm.id} onClick={() => openMessage({ id: tm.id, from: tm.from, subject: tm.subject, thread_id: sel.thread_id })}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-virgo-navy-700 ${sel.id === tm.id ? 'bg-virgo-navy-700' : ''}`}>
                        <span className="text-virgo-teal-200">{tm.from}</span><span className="text-virgo-teal-600"> · {fmtDate(tm.received_at)}</span>
                        <div className="truncate text-virgo-teal-500 text-xs">{tm.subject || '(no subject)'}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {folderPicker && (
        <Overlay onClose={() => setFolderPicker(false)} title="File to a folder" icon={<FolderInput size={16} />}>
          <FolderPicker rail={rail} onPick={moveMsg}
            newFolder={newFolder} setNewFolder={setNewFolder} onCreate={createFolder} />
        </Overlay>
      )}
      {bulkPicker && (
        <Overlay onClose={() => setBulkPicker(false)} title={matchAll ? `Move ALL ${matchAll.expected.toLocaleString()} matching to…` : `Move ${selected.size} selected to…`} icon={<FolderInput size={16} />}>
          <FolderPicker rail={rail} onPick={(t, l) => { setBulkPicker(false); bulkMove(t, l); }}
            newFolder={newFolder} setNewFolder={setNewFolder} onCreate={createFolder} />
        </Overlay>
      )}
      {guardPrompt && (
        <Overlay onClose={() => setGuardPrompt(null)} title="Known contacts in this batch" icon={<AlertTriangle size={16} />}>
          <div className="text-sm text-virgo-teal-200 space-y-3">
            <p><b>{guardPrompt.detail.count}</b> message{guardPrompt.detail.count === 1 ? ' is' : 's are'} from known contacts:</p>
            <ul className="text-xs text-virgo-teal-400 space-y-0.5">
              {(guardPrompt.detail.sample_senders || []).map((a) => <li key={a}>{a}</li>)}
            </ul>
            <p className="text-xs text-virgo-teal-400">At bulk scale a filter mistake takes client mail with it.
              Nothing was moved. Proceeding sends these to {guardPrompt.label || guardPrompt.target} anyway.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setGuardPrompt(null)} className="px-3 py-1.5 rounded bg-virgo-navy-600 text-virgo-teal-200">Cancel</button>
              <button onClick={() => bulkMove(guardPrompt.target, guardPrompt.label, true, guardPrompt.ids)}
                className="px-3 py-1.5 rounded bg-red-600/80 text-white hover:bg-red-600">
                {guardPrompt.label || guardPrompt.target} anyway — deliberately
              </button>
            </div>
          </div>
        </Overlay>
      )}
      {filePicker && <Overlay onClose={() => setFilePicker(false)} title="File to a matter (moves out of inbox, kept under the matter)" icon={<Briefcase size={16} />}><MatterPicker cases={cases} onPick={fileTo} /></Overlay>}
      {action && (
        <Overlay onClose={() => setAction(null)} title={action.type === 'time' ? 'Log time to matter' : 'Post to case feed'} icon={<Briefcase size={16} />}>
          <div className="space-y-2">
            {action.type === 'time' ? (
              <>
                <input type="number" step="0.1" value={action.hours} onChange={(e) => setAction({ ...action, hours: e.target.value })} placeholder="Hours (e.g. 0.5)" className="w-full text-sm rounded-lg px-3 py-2 bg-virgo-navy-900 border border-virgo-teal-700/50" />
                <input value={action.description} onChange={(e) => setAction({ ...action, description: e.target.value })} placeholder="Description" className="w-full text-sm rounded-lg px-3 py-2 bg-virgo-navy-900 border border-virgo-teal-700/50" />
              </>
            ) : (
              <textarea value={action.content} onChange={(e) => setAction({ ...action, content: e.target.value })} rows={5} placeholder="Post a note to the case feed…" className="w-full text-sm rounded-lg px-3 py-2 bg-virgo-navy-900 border border-virgo-teal-700/50" />
            )}
            {action.error && <div className="text-sm text-red-300 flex gap-2"><AlertCircle size={15} className="shrink-0 mt-0.5" />{action.error}</div>}
            <div className="flex justify-end"><button onClick={submitAction} className="px-4 py-1.5 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white text-sm">Save</button></div>
          </div>
        </Overlay>
      )}
      {compose && (
        <Overlay onClose={() => setCompose(null)} title={compose.sent ? 'Delivery status' : 'New message'} icon={<Edit3 size={16} />} wide>
          {!compose.sent ? (
            <div className="space-y-2">
              <input value={compose.to} onChange={(e) => setCompose({ ...compose, to: e.target.value })} placeholder="To (comma-separated)" className="w-full text-sm rounded-lg px-3 py-2 bg-virgo-navy-900 border border-virgo-teal-700/50" />
              <input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} placeholder="Subject" className="w-full text-sm rounded-lg px-3 py-2 bg-virgo-navy-900 border border-virgo-teal-700/50" />
              <textarea value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} rows={12} placeholder="Write your message…" className="w-full text-sm rounded-lg px-3 py-2 bg-virgo-navy-900 border border-virgo-teal-700/50 font-sans" />
              {compose.error && <div className="text-sm text-red-300 flex gap-2 bg-red-500/10 border border-red-500/30 rounded-lg p-2"><AlertCircle size={15} className="shrink-0 mt-0.5" />{compose.error}</div>}
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-virgo-teal-500">Signature auto-inserted · honest per-recipient delivery status · no read-tracking</span>
                <button onClick={sendCompose} disabled={compose.status === 'sending'} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 disabled:opacity-60 text-white text-sm font-medium">
                  {compose.status === 'sending' ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send</>}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-virgo-teal-300">Message sent. Per-recipient delivery status:</div>
              <div className="space-y-1.5">
                {(compose.sent.recipients || []).map((r) => <RecipientStatus key={r.email} r={r} />)}
              </div>
              {compose.sent.telemetry_ok === false && (
                <div className="text-[11px] text-virgo-gold-400">Delivery confirmation is momentarily unavailable — status shows what the server confirmed; it advances to delivered/bounced once the delivery log is reachable. Nothing is assumed.</div>
              )}
              <div className="flex justify-end"><button onClick={() => setCompose(null)} className="px-4 py-1.5 rounded-lg bg-virgo-navy-700 hover:bg-virgo-navy-600 text-virgo-teal-200 text-sm">Done</button></div>
            </div>
          )}
        </Overlay>
      )}
      {showSheet && (
        <Overlay onClose={() => setShowSheet(false)} title="Keyboard shortcuts" icon={<Command size={16} />}>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
            {[['j / k', 'Move down / up'], ['Enter', 'Open message'], ['r', 'Reply'], ['c', 'Compose'], ['/', 'Search all mail'], ['e', 'Archive (with M1)'], ['?', 'This sheet'], ['Esc', 'Close']].map(([k, d]) => (
              <div key={k} className="flex items-center gap-2"><kbd className="px-1.5 py-0.5 bg-virgo-navy-900 rounded text-virgo-teal-300 text-xs border border-virgo-teal-700/50">{k}</kbd><span className="text-virgo-teal-300">{d}</span></div>
            ))}
          </div>
        </Overlay>
      )}
      {toast && <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-lg bg-virgo-navy-700 border border-virgo-teal-700/50 text-sm text-virgo-teal-100 shadow-2xl flex items-center gap-2"><CornerUpLeft size={14} className="text-virgo-teal-400" />{toast}</div>}
    </div>
  );
}

function Overlay({ title, icon, children, onClose, wide }) {
  return (
    <div className="absolute inset-0 z-50 flex items-start justify-center pt-24 bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className={`w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} bg-virgo-navy-800 rounded-xl border border-virgo-teal-700/50 shadow-2xl`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-virgo-teal-700/40"><span className="flex items-center gap-2 text-white font-medium">{icon}{title}</span><button onClick={onClose} className="text-virgo-teal-400 hover:text-white"><X size={16} /></button></div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
const DELIVERY_STYLE = {
  delivered: ['bg-emerald-500/20 text-emerald-300', 'Delivered'],
  bounced: ['bg-red-500/20 text-red-300', 'Bounced'],
  rejected: ['bg-red-500/20 text-red-300', 'Rejected'],
  failed: ['bg-red-500/20 text-red-300', 'Failed'],
  deferred: ['bg-virgo-gold-400/20 text-virgo-gold-400', 'Deferred'],
  transmitted: ['bg-cyan-500/20 text-cyan-300', 'Transmitted'],
  queued: ['bg-cyan-500/20 text-cyan-300', 'Queued'],
};
function RecipientStatus({ r }) {
  const [cls, label] = DELIVERY_STYLE[r.status] || ['bg-slate-500/20 text-slate-300', r.status];
  const pending = r.confirmation_pending;
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="truncate text-virgo-teal-200">{r.email}</span>
      <span className="flex items-center gap-2 shrink-0">
        {r.detail && <span className="text-[11px] text-virgo-teal-600 truncate max-w-[12rem]">{r.detail}</span>}
        <span className={`text-[11px] px-2 py-0.5 rounded ${pending ? 'bg-virgo-gold-400/20 text-virgo-gold-400' : cls}`}>
          {pending ? 'Transmitted \u00b7 confirmation pending' : label}
        </span>
      </span>
    </div>
  );
}
function MatterPicker({ cases, onPick }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const arr = s ? cases.filter((c) => `${c.case_name || c.name || ''} ${c.display_number || c.case_number || ''} ${c.client_name || ''}`.toLowerCase().includes(s)) : cases;
    return arr.slice(0, 40);
  }, [q, cases]);
  return (
    <div>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search matters…" className="w-full text-sm rounded-lg px-3 py-2 mb-2 bg-virgo-navy-900 border border-virgo-teal-700/50" />
      <div className="max-h-72 overflow-y-auto space-y-0.5">
        {filtered.length === 0 && <div className="text-sm text-virgo-teal-500 p-2">No matching matter.</div>}
        {filtered.map((c) => (
          <button key={c.id} onClick={() => onPick(c)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-virgo-navy-700">
            <div className="text-sm text-white flex items-center gap-2"><StatusDot status={c.status} />{c.case_name || c.name}</div>
            <div className="text-xs text-virgo-teal-500">{c.display_number || c.case_number}{c.client_name ? ` · ${c.client_name}` : ''}</div>
          </button>
        ))}
      </div>
    </div>
  );
}


// Rail label for the header line: prefer the server's display name ("Deleted
// Items"), fall back to the folder key.
function railLabel(rail, key) {
  const all = [...(rail.roles || []), ...(rail.folders || []), ...(rail.diagnostics || [])];
  const hit = all.find((x) => x.key === key);
  if (hit) return hit.name;
  return key ? key[0].toUpperCase() + key.slice(1) : '';
}

function FolderGroup({ items, folder, inSearch, onPick, onDropIds }) {
  const [over, setOver] = useState(null);
  return items.map((f) => {
    const Icon = ROLE_ICON[f.role || f.key] || Folder;
    const active = folder === f.key && !inSearch;
    const droppable = !!onDropIds && f.key !== folder;
    return (
      <button key={f.id || f.key} onClick={() => onPick(f.key)}
        onDragOver={droppable ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOver(f.key); } : undefined}
        onDragLeave={droppable ? () => setOver((o) => (o === f.key ? null : o)) : undefined}
        onDrop={droppable ? (e) => {
          e.preventDefault(); setOver(null);
          try {
            const ids = JSON.parse(e.dataTransfer.getData('application/x-ouija-ids') || '[]');
            if (ids.length) onDropIds(f, ids);
          } catch { /* not our payload */ }
        } : undefined}
        title={`${f.name} — ${f.mirror_count} in the mirror`}
        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg ${over === f.key ? 'ring-2 ring-virgo-teal-400 bg-virgo-teal-700/30 text-white' : active ? 'bg-virgo-teal-700/40 text-white' : 'text-virgo-teal-300 hover:bg-virgo-navy-700'}`}>
        <Icon size={15} className="shrink-0" />
        <span className="flex-1 text-left truncate">{f.name}</span>
        <span className={`text-xs shrink-0 ${active ? 'text-virgo-teal-300' : 'text-virgo-teal-600'}`}>
          {f.mirror_count || ''}
        </span>
      </button>
    );
  });
}

function FolderPicker({ rail, onPick, newFolder, setNewFolder, onCreate }) {
  const [q, setQ] = useState('');
  const all = [...(rail.roles || []), ...(rail.folders || [])];
  const s = q.trim().toLowerCase();
  const shown = (s ? all.filter((f) => f.name.toLowerCase().includes(s)) : all);
  return (
    <div>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search folders…"
        className="w-full text-sm rounded-lg px-3 py-2 mb-2 bg-virgo-navy-900 border border-virgo-teal-700/50" />
      <div className="max-h-72 overflow-y-auto space-y-0.5">
        {shown.length === 0 && <div className="text-sm text-virgo-teal-500 p-2">No folder matches.</div>}
        {shown.map((f) => (
          <button key={f.id || f.key} onClick={() => onPick(f.id || f.key, f.name)}
            className="w-full text-left px-3 py-2 rounded-lg hover:bg-virgo-navy-700 flex items-center gap-2">
            <Folder size={14} className="text-virgo-teal-500 shrink-0" />
            <span className="flex-1 truncate text-sm text-white">{f.name}</span>
            <span className="text-xs text-virgo-teal-600">{f.mirror_count || ''}</span>
          </button>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-virgo-teal-700/30 flex items-center gap-2">
        <FolderPlus size={15} className="text-virgo-teal-500 shrink-0" />
        <input value={newFolder} onChange={(e) => setNewFolder(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onCreate(); }}
          placeholder="New folder for a matter…"
          className="flex-1 text-sm rounded-lg px-3 py-1.5 bg-virgo-navy-900 border border-virgo-teal-700/50" />
        <button onClick={onCreate} disabled={!newFolder.trim()}
          className="px-3 py-1.5 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 disabled:opacity-40 text-white text-sm">
          Create{' '}&amp; file
        </button>
      </div>
    </div>
  );
}
